// ===========================================
// Authentication Context - Types & Context
// ===========================================
// Separated to avoid react-refresh warnings

import { createContext } from 'react';

// Types
export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'ADMIN' | 'TEACHER' | 'PARENT' | 'STUDENT';
  schoolId: string;
  schoolName: string;
  isAlsoTeacher?: boolean; // True if admin user also has a teacher record
  isStaff?: boolean;       // True if user has a custom staff role
  /** Roles slice 2: an ADMIN marked as a support account (still the owner tier, not the office). */
  isSupportAccount?: boolean;
  /** Sent by login and /auth/me as `role === 'ADMIN'`; never read to grant (use `isOwner`). Kept
   *  on the wire for clients cached from before slice 1a; drop it with the column (roles plan §8). */
  isSuperAdmin?: boolean;
  isAlsoParent?: boolean;  // True if user also has a parent record
  familyId?: string;       // Set for any parent login with a family (#442)
  teacherId?: string;      // Teacher record id if user has one
  /**
   * #442: the server will refuse this user (403 CONSENT_REQUIRED) until they agree to the
   * school's T&Cs. `ProtectedRoute` then shows `ConsentGate` instead of the app.
   */
  requiresConsentAcknowledgement?: boolean;
  /** PRIMARY = 3 steps incl. media; CONTACT = T&Cs + Privacy; AWAITING_PRIMARY = paused (D4). */
  consentKind?: 'PRIMARY' | 'CONTACT' | 'AWAITING_PRIMARY';
  staffRole?: {
    id: string;
    name: string;
    color?: string;
    permissions: string[];
  };
}

export interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

export interface LoginCredentials {
  email: string;
  password: string;
  schoolSlug?: string;
  rememberMe?: boolean;
}

export interface LoginResponse {
  status: string;
  data: {
    accessToken: string;
    refreshToken: string;
    user: User;
    requires2FA?: boolean;
    tempToken?: string;
    requiresConsentAcknowledgement?: boolean;
  };
}

/**
 * Error thrown when login requires 2FA verification
 */
export class TwoFactorRequiredError extends Error {
  tempToken: string;
  constructor(tempToken: string) {
    super('Two-factor authentication required');
    this.name = 'TwoFactorRequiredError';
    this.tempToken = tempToken;
  }
}

export interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<User>;
  completeLogin: (data: { accessToken: string; refreshToken: string; user: User }) => Promise<User>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

// Create context - exported for useAuth hook
export const AuthContext = createContext<AuthContextType | undefined>(undefined);
